/**
 * @fileoverview ตัวช่วยที่หน้ารายงานของพอร์ทัลลูกค้าใช้ร่วมกัน
 *
 * ทุกหน้ารายงานดึงข้อมูลจาก `customer/reports/*` ซึ่งคืนค่าเป็นตัวเลข/ISO date ดิบ ๆ
 * การจัดรูปแบบให้อ่านง่ายทำที่ฝั่งเว็บที่เดียวตรงนี้ เพื่อให้ทุกหน้าแสดงผลเหมือนกัน
 *
 * @module utils/customerReport
 */

import { t } from '../components/CustomComponent.js';

/** ช่วงเวลาที่ dropdown ของทุกหน้ารายงานเลือกได้ (ค่าต้องตรงกับ PERIODS ฝั่ง backend) */
export const REPORT_PERIODS = [
  { value: 'this_month', label: 'this_month' },
  { value: 'last_30_days', label: 'last_30_days' },
  { value: 'last_90_days', label: 'last_90_days' },
  { value: 'last_6_months', label: 'last_6_months' },
  { value: 'last_12_months', label: 'last_12_months' },
];

/** @param {string} dateStr ISO date */
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

/** เดือนของกราฟ: "2026-08" -> "Aug 26" */
export const formatMonthLabel = (month) => {
  if (!month) return '';
  const [year, monthNumber] = String(month).split('-');
  const date = new Date(Number(year), Number(monthNumber) - 1, 1);
  if (isNaN(date.getTime())) return month;
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

/** นาที -> "2h 30m" (backend ส่งเป็นนาทีเสมอเพื่อให้คำนวณต่อได้) */
export const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || !Number.isFinite(Number(minutes))) return '-';
  const total = Math.round(Number(minutes));
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

export const formatNumber = (value, digits = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return numeric.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatMoney = (value) => formatNumber(value, 2);

export const formatPercent = (value, digits = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric.toFixed(digits)}%`;
};

/** แปลงค่าที่เป็น key ภาษาอังกฤษจาก backend เป็นข้อความตามภาษาที่เลือก (ไม่มีคำแปลก็คืนค่าเดิมแบบอ่านง่าย) */
export const translateKey = (value) => {
  if (!value) return '-';
  const translated = t(value);
  return translated === value ? String(value).replaceAll('_', ' ') : translated;
};

/** สีของ badge ตามสถานะที่ backend ส่งมา - ใช้ชุดเดียวกันทุกหน้ารายงาน */
export const STATUS_COLOR = {
  operational: 'success',
  active: 'success',
  completed: 'success',
  success: 'success',
  maintenance: 'warning',
  expiring: 'warning',
  in_progress: 'warning',
  pending: 'warning',
  pending_approval: 'warning',
  pending_approve: 'warning',
  follow_up: 'warning',
  issue: 'danger',
  expired: 'danger',
  rejected: 'danger',
  cancel: 'danger',
  cancelled: 'danger',
  draft: 'secondary',
  open: 'primary',
  approved: 'primary',
  assigned: 'primary',
  scheduled: 'primary',
};

export const statusColor = (status) => STATUS_COLOR[String(status || '').toLowerCase()] || 'secondary';

/** จานสีของกราฟ - ล้อกับสี bootstrap ที่ใช้บนการ์ด KPI */
export const CHART_COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];

const customerReport = {
  REPORT_PERIODS,
  formatDate,
  formatTime,
  formatMonthLabel,
  formatMinutes,
  formatNumber,
  formatMoney,
  formatPercent,
  translateKey,
  statusColor,
  CHART_COLORS,
};

export default customerReport;
